import React, { useEffect, useState, useCallback } from 'react';
import Topbar from '../component/Topbar';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { BiRefresh, BiPlug, BiCheckCircle, BiXCircle, BiErrorCircle } from 'react-icons/bi';
import { acquireGetAccessToken } from '../utils/GetGrapApi';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { Buffer } from 'buffer';
import { checkRole, UserRole } from '../callMsGroup';
import { saveRealRole, getEffectiveRole } from '../utils/roleOverride';
import LoadingScreen from '../component/LoadingScreen';
import ReactPaginate from 'react-paginate';
import moment from 'moment-timezone';

interface WebhookLog {
    id: number;
    receivedAt: string;
    source: string | null;
    totalReceived: number;
    inserted: number;
    updated: number;
    failed: number;
    status: 'success' | 'partial' | 'error';
    errorMessage: string | null;
    ipAddress: string | null;
}

const PAGE_SIZE = 20;

export default function WebhookLogs() {
    const { inProgress, instance, accounts } = useMsal();
    const navigate = useNavigate();

    const [token, setToken] = useState('');
    const [tokenProfile, setTokenProfile] = useState('');
    const [photoData, setPhotoData] = useState('');
    const [role, setRole] = useState<UserRole>('owner');
    const [stateLoad, setStateLoad] = useState(false);

    const [logs, setLogs] = useState<WebhookLog[]>([]);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [loading, setLoading] = useState(false);

    const auths = useCallback(() => ({
        headers: { Authorization: 'Bearer ' + sessionStorage.getItem('accessToken') },
    }), []);

    const loadLogs = useCallback(async (page = 0) => {
        setLoading(true);
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_END_POINT}/v1/Webhook/logs`,
                { ...auths(), params: { offset: page * PAGE_SIZE, limit: PAGE_SIZE } }
            );
            setLogs(res.data.logs ?? []);
            setTotal(res.data.total ?? 0);
        } catch {
            setLogs([]);
        } finally {
            setLoading(false);
        }
    }, [auths]);

    useEffect(() => {
        if (!token && inProgress === InteractionStatus.None) {
            acquireGetAccessToken(instance, accounts).then(async (res: any) => {
                if (!res) return;
                setToken(res.accessToken);
                setTokenProfile(res.accessTokenProfile);
                const r1 = await axios('https://graph.microsoft.com/v1.0/me/photo/$value', {
                    headers: { Authorization: `Bearer ${res.accessTokenProfile}` },
                    responseType: 'arraybuffer',
                });
                setPhotoData('data:image/jpeg;base64, ' + Buffer.from(r1.data, 'binary').toString('base64'));
            });
        }
    }, [token, inProgress, instance, accounts]);

    useEffect(() => {
        if (!tokenProfile) return;
        checkRole(tokenProfile).then((userRole) => {
            saveRealRole(userRole);
            setRole(getEffectiveRole(userRole));
            if (userRole !== 'superadmin') navigate('/accessdenied');
            else setStateLoad(true);
        });
    }, [tokenProfile, navigate]);

    useEffect(() => {
        if (!token) return;
        loadLogs(currentPage);
    }, [token, currentPage, loadLogs]);

    const handlePageClick = (e: any) => setCurrentPage(e.selected);
    const totalPages = Math.ceil(total / PAGE_SIZE);

    const StatusBadge = ({ status }: { status: WebhookLog['status'] }) => {
        if (status === 'success') return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: '#dcfce7', color: '#166534' }}>
                <BiCheckCircle size={13} /> Success
            </span>
        );
        if (status === 'partial') return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: '#fef9c3', color: '#854d0e' }}>
                <BiErrorCircle size={13} /> Partial
            </span>
        );
        return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: '#fee2e2', color: '#991b1b' }}>
                <BiXCircle size={13} /> Error
            </span>
        );
    };

    if (!stateLoad) return <LoadingScreen />;

    return (
        <div className="admin-layout">
            <Topbar token={token} photoData={photoData} role={role} />
            <main className="admin-main">
                <div className="dt-container">
                    {/* Header */}
                    <div className="dt-header">
                        <div>
                            <div className="dt-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <BiPlug size={22} /> Webhook Logs
                            </div>
                            <div className="dt-subtitle">Employee data received from external systems</div>
                        </div>
                        <button
                            className="btn btn-add"
                            onClick={() => loadLogs(currentPage)}
                            disabled={loading}
                        >
                            <BiRefresh size={16} className={loading ? 'spin' : ''} />
                            {loading ? ' Loading…' : ' Refresh'}
                        </button>
                    </div>

                    {/* Table */}
                    <div className="dt-table-wrap">
                        <table className="dt-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 170 }}>Received At</th>
                                    <th style={{ width: 140 }}>Source</th>
                                    <th style={{ width: 80, textAlign: 'center' }}>Total</th>
                                    <th style={{ width: 80, textAlign: 'center' }}>Inserted</th>
                                    <th style={{ width: 80, textAlign: 'center' }}>Updated</th>
                                    <th style={{ width: 80, textAlign: 'center' }}>Failed</th>
                                    <th style={{ width: 110 }}>Status</th>
                                    <th style={{ width: 120 }}>IP Address</th>
                                    <th>Error</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="dt-empty">
                                            <div>No webhook logs yet</div>
                                            <small>Logs will appear here when an external system posts employee data</small>
                                        </td>
                                    </tr>
                                ) : logs.map((log) => (
                                    <tr key={log.id}>
                                        <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                                            {moment.utc(log.receivedAt).tz('Asia/Bangkok').format('DD-MMM-YYYY HH:mm:ss')}
                                        </td>
                                        <td>
                                            <span style={{ fontSize: 13, color: '#374151' }}>{log.source || '—'}</span>
                                        </td>
                                        <td style={{ textAlign: 'center', fontWeight: 600 }}>{log.totalReceived}</td>
                                        <td style={{ textAlign: 'center', color: '#166534', fontWeight: 600 }}>{log.inserted}</td>
                                        <td style={{ textAlign: 'center', color: '#1d4ed8', fontWeight: 600 }}>{log.updated}</td>
                                        <td style={{ textAlign: 'center', color: log.failed > 0 ? '#991b1b' : '#6b7280', fontWeight: 600 }}>{log.failed}</td>
                                        <td><StatusBadge status={log.status} /></td>
                                        <td style={{ fontSize: 12, color: '#6b7280' }}>{log.ipAddress || '—'}</td>
                                        <td style={{ fontSize: 12, color: '#991b1b', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                            title={log.errorMessage ?? undefined}>
                                            {log.errorMessage || '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Footer */}
                    <div className="dt-footer">
                        <div className="dt-count">
                            Showing {total > 0 ? `${currentPage * PAGE_SIZE + 1}–${Math.min((currentPage + 1) * PAGE_SIZE, total)}` : '0'} of {total} logs
                        </div>
                        {totalPages > 1 && (
                            <ReactPaginate
                                previousLabel="‹"
                                nextLabel="›"
                                pageClassName="page-item"
                                pageLinkClassName="page-link"
                                previousClassName="page-item"
                                previousLinkClassName="page-link"
                                nextClassName="page-item"
                                nextLinkClassName="page-link"
                                breakLabel="..."
                                breakClassName="page-item"
                                breakLinkClassName="page-link"
                                pageCount={totalPages}
                                marginPagesDisplayed={1}
                                pageRangeDisplayed={4}
                                onPageChange={handlePageClick}
                                containerClassName="pagination mb-0"
                                activeClassName="active"
                                forcePage={currentPage}
                            />
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}
